import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { getCancellationPreview, cancelBooking } from '../api';

// Owner Bookings & Cancellation v2 doc, Story 1/2: a reason is required, and the refund breakdown
// is shown *before* the customer/owner confirms, not just after - fetches the read-only preview
// (same computeRefundBreakdown the real cancel() call uses, so this can never show a different
// number than what actually happens) as soon as the modal opens.
export default function CancelBookingModal({ bookingId, show, onHide, onCancelled }) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [reason, setReason] = useState('');
  const [forceMajeure, setForceMajeure] = useState(false);
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !bookingId) return;
    setPreview(null);
    setPreviewFailed(false);
    setReason('');
    setForceMajeure(false);
    setEvidenceUrl('');
    setTermsAccepted(false);
    setError('');
    setLoadingPreview(true);
    getCancellationPreview(bookingId)
      .then(setPreview)
      .catch(() => setPreviewFailed(true))
      .finally(() => setLoadingPreview(false));
  }, [show, bookingId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('A cancellation reason is required.');
      return;
    }
    if (!termsAccepted) {
      setError('Please confirm you have read and accept the cancellation terms.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await cancelBooking(bookingId, {
        reason: reason.trim(),
        forceMajeureClaimed: forceMajeure,
        forceMajeureEvidenceUrl: forceMajeure ? evidenceUrl.trim() : undefined
      });
      onCancelled(result);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to cancel this booking.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Cancel this booking?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingPreview && <Spinner animation="border" size="sm" role="status" />}
          <div className="mb-3 p-2 rounded bg-light small">
            <div className="fw-semibold mb-1">Cancellation terms</div>
            {preview ? (
              <>
                <div className="mb-1">
                  {preview.withinRefundWindow ? 'Within the refund window - full refund' : `Outside the refund window - ${Math.round(preview.forfeitRate * 100)}% forfeited`}
                </div>
                <div>Rental amount refunded: ₹{preview.rentalAmountRefunded}</div>
                <div>Platform fee refunded: ₹{preview.platformFeeRefunded} {preview.platformFeeRefunded === 0 && '(never refunded on a customer cancellation)'}</div>
                <div>Delivery charge refunded: ₹{preview.deliveryChargeRefunded}</div>
                <div>Deposit refunded: ₹{preview.depositRefunded}</div>
              </>
            ) : previewFailed ? (
              <div>The refund breakdown couldn't be loaded, but forfeiture rules may still apply depending on how close to the start date this cancellation is.</div>
            ) : null}
          </div>
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          <Form.Group className="mb-2" controlId="cancel-booking-reason">
            <Form.Label>Reason for cancelling</Form.Label>
            <Form.Control as="textarea" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} required maxLength={500} />
          </Form.Group>
          <Form.Check
            type="checkbox"
            id="force-majeure-check"
            label="This is due to a documented emergency (medical, government order, bereavement)"
            checked={forceMajeure}
            onChange={(e) => setForceMajeure(e.target.checked)}
            className="mb-2"
          />
          {forceMajeure && (
            <Form.Group className="mb-2" controlId="cancel-booking-evidence-url">
              <Form.Label className="small">Link to supporting evidence (optional, helps Finance review faster)</Form.Label>
              <Form.Control type="url" value={evidenceUrl} onChange={(e) => setEvidenceUrl(e.target.value)} placeholder="https://..." />
            </Form.Group>
          )}
          <Form.Check
            type="checkbox"
            id="cancel-terms-check"
            label="I have read and accept the cancellation terms above"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Keep booking
          </Button>
          <Button variant="danger" type="submit" disabled={submitting || !termsAccepted}>
            {submitting ? 'Cancelling…' : 'Confirm cancellation'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
